import React, { useState, useCallback, useEffect } from 'react';
import { EditorProvider, useCurrentEditor, EditorContent } from '@tiptap/react';
import { extensions } from '../../../../../util/tiptapExtensions';
import styles from '../../../BlogTab/BlogPostPage/BlogPost.module.scss'
import '../../../../../scss/TipTap/tiptap-global.scss'
import MenuBar from './MenuBar';
import HeaderInputs from './HeaderInputs';
import { blogTemplate } from '../../../../../util/blogTemplate';
import ConfirmPopup from '../../../../../Components/ConfirmPopupComponent/ConfirmPopup';

const SaveButton = ({ onSave, buttonText = "Stage Post" }) => {
  const { editor } = useCurrentEditor();

  const handleSave = () => {
    if (!editor) return;
    onSave(editor);
  };

  return (
    <button 
      className={styles.saveButton}
      onClick={handleSave}
    >
      {buttonText}
    </button>
  );
};

const BlogEditor = ({ initialTitle = '', initialContent = '', initialCategory = '', onSave, isSaving, buttonText }) => {
  const [title, setTitle] = useState(initialTitle || blogTemplate.title);
  const [date, setDate] = useState(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
  const [category, setCategory] = useState(initialCategory || 'Hardware Upgrades');
  const [message, setMessage] = useState('');
  const [editor, setEditor] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize editor with custom nodes
  const initializeEditor = useCallback((editor) => {
    if (!editor || isInitialized) return;

    const content = initialContent || blogTemplate.content;
    editor.commands.setContent(content);

    // Ensure our custom nodes exist
    let hasTitle = false;
    let hasDate = false;
    let hasCategory = false;

    editor.state.doc.descendants((node) => {
      if (node.type.name === 'blogTitle') hasTitle = true;
      if (node.type.name === 'blogDate') hasDate = true;
      if (node.type.name === 'blogCategory') hasCategory = true;
    });

    // Create missing nodes if necessary
    if (!hasTitle) {
      editor.commands.insertContent([{
        type: 'blogTitle',
        content: [{ type: 'text', text: title }]
      }], { at: 0 });
    }

    if (!hasDate) {
      editor.commands.insertContent([{
        type: 'blogDate',
        content: [{ type: 'text', text: date }]
      }]);
    }

    if (!hasCategory) {
      editor.commands.insertContent([{
        type: 'blogCategory',
        content: [{ type: 'text', text: category }]
      }]);
    }

    setIsInitialized(true);
  }, [title, date, category, initialContent, isInitialized]);

  useEffect(() => {
    if (editor && !isInitialized) {
      initializeEditor(editor);
    }
  }, [editor, initializeEditor, isInitialized]);

  const updateEditorField = useCallback((field, value) => {
    if (!editor) return;

    const nodeType = {
      title: 'blogTitle',
      date: 'blogDate',
      category: 'blogCategory'
    }[field];

    if (!nodeType) return;

    // Find all instances of the node type
    const nodes = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === nodeType) {
        nodes.push({ node, pos });
      }
    });

    // Handle multiple instances of the same node type
    if (nodes.length > 1) {
      // Keep only the first instance and remove others
      const [first, ...rest] = nodes;
      rest.forEach(({ pos, node }) => {
        editor
          .chain()
          .focus()
          .deleteRange({ from: pos, to: pos + node.nodeSize })
          .run();
      });
      // Update the content of the first instance
      const start = first.pos + 1;
      const end = first.pos + first.node.nodeSize - 1;
      editor.view.dispatch(
        editor.view.state.tr
          .delete(start, end)
          .insertText(value, start)
      );
    } else if (nodes.length === 1) {
      // Update the single instance
      const { pos, node } = nodes[0];
      const start = pos + 1;
      const end = pos + node.nodeSize - 1;
      editor.view.dispatch(
        editor.view.state.tr
          .delete(start, end)
          .insertText(value, start)
      );
    } else {
      // No node found, create it at the appropriate position
      let insertPos = 0;
      
      if (nodeType === 'blogTitle') {
        // Find the heroContent div
        editor.state.doc.descendants((node, pos) => {
          if (node.type.name === 'paragraph' && node.textContent.includes('heroContent')) {
            insertPos = pos + node.nodeSize;
            return false;
          }
        });
      }

      editor
        .chain()
        .focus()
        .insertContentAt(insertPos, [{
          type: nodeType,
          content: [{ type: 'text', text: value }]
        }])
        .run();
    }
  }, [editor]);

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    // Defer the editor update to maintain input focus
    setTimeout(() => updateEditorField('title', newTitle), 0);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
    // Defer the editor update to maintain input focus
    setTimeout(() => updateEditorField('date', newDate), 0);
  };

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setCategory(newCategory);
    // Defer the editor update to maintain input focus
    setTimeout(() => updateEditorField('category', newCategory), 0);
  };

  const handleSave = (editor) => {
    if (!title.trim()) {
      setMessage('Please enter a title');
      return;
    }

    const content = editor.getHTML();
    onSave({ title, content, category });
  };

  const handleEditorUpdate = ({ editor: newEditor }) => {
    if (!newEditor) return;
    
    // Only update the editor reference if it changed
    if (newEditor !== editor) {
      setEditor(newEditor);
    }

    // Track which fields we've updated to avoid duplicates
    const foundFields = new Set();

    // First, look for nodes at the top level
    newEditor.state.doc.descendants((node, pos) => {
      if (foundFields.size === 3) return false; // Exit early if we found all fields
      
      const text = node.textContent;
      
      switch (node.type.name) {
        case 'blogTitle':
          if (!foundFields.has('title')) {
            setTitle(text);
            foundFields.add('title');
          }
          break;
        case 'blogDate':
          if (!foundFields.has('date')) {
            setDate(text);
            foundFields.add('date');
          }
          break;
        case 'blogCategory':
          if (!foundFields.has('category')) {
            setCategory(text);
            foundFields.add('category');
          }
          break;
      }
    });

    // Log if any fields weren't found
    if (!foundFields.has('title')) console.warn('blogTitle node not found');
    if (!foundFields.has('date')) console.warn('blogDate node not found');
    if (!foundFields.has('category')) console.warn('blogCategory node not found');
  };

  return (
    <div className={styles.blogEditorContainer}>
      {message && <div className={styles.message}>{message}</div>}
      <ConfirmPopup />
      
      <HeaderInputs
        title={title}
        date={date}
        category={category}
        onTitleChange={handleTitleChange}
        onDateChange={handleDateChange}
        onCategoryChange={handleCategoryChange}
      />
      
      <div className={styles.editorSection}>
        <EditorProvider
          slotBefore={<MenuBar />}
          extensions={extensions}
          onCreate={({ editor }) => {
            setEditor(editor);
          }}
          content={blogTemplate.content}
          onUpdate={handleEditorUpdate}
        >
          <div className={styles.editorContent}>
            <EditorContent />
          </div>
          <div className={styles.buttonContainer}>
            <SaveButton onSave={handleSave} buttonText={buttonText} />
          </div>
        </EditorProvider>
      </div>
    </div>
  );
};

export default BlogEditor;
